import { Button, Skeleton, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EPCClassification } from "legacyNext/components";
import { useAssetIntensityQuery } from "legacyNext/hooks";
import { NextProperty } from "legacyNext/types";
import { formatNumberLocale } from "utils/report.helpers";

import { S } from "./asset-marker.styles";

type Props = {
    asset: NextProperty;
};

export const AssetMarketContent = (props: Props) => {
    const navigate = useNavigate();
    const { asset } = props;

    const { t } = useTranslation();

    const { data, loading } = useAssetIntensityQuery(asset.assetId);
    const intensity = useMemo(() => {
        if (!data) {
            return "-";
        }

        const value = formatNumberLocale(data.value, t);
        return `${value} ${data.unit}`;
    }, [data, t]);

    const handleSelectProperty = () => {
        navigate(`/next/asset/${asset.assetId}`);
    };

    return (
        <S.InfoWindowContent>
            <S.Heading>
                <Typography fontWeight="bold">{asset.fullAddress}</Typography>
                <S.AssetIdentity>
                    <Typography>{`BFE ${asset.assetExternalId}`}</Typography>
                </S.AssetIdentity>
            </S.Heading>
            <S.AssetPropertyList>
                <Typography>{t("legacyNext.area", "area")}</Typography>
                <Typography textAlign="right">
                    {t("legacyNext.sqm", "sqm", {
                        value: asset.area,
                    })}
                </Typography>

                <Typography>
                    {t("common.labels.intensity", "intensity")}
                </Typography>
                {loading ? (
                    <Skeleton variant="text" />
                ) : (
                    <Typography textAlign="right">{intensity}</Typography>
                )}
                <Typography>EPC</Typography>
                <S.AssetPropertyEPC>
                    <EPCClassification
                        assetExternalId={asset.assetExternalId}
                    />
                </S.AssetPropertyEPC>
            </S.AssetPropertyList>
            <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={handleSelectProperty}
            >
                {t("legacyNext.selectProperty", "select property")}
            </Button>
        </S.InfoWindowContent>
    );
};
